import { useRef, useEffect } from 'react'

export default function NeuralNetwork() {
	const canvasRef = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas) return

		const ctx = canvas.getContext('2d')
		if (!ctx) return

		// Set canvas to full width and height of parent
		const resize = () => {
			const parent = canvas.parentElement
			if (!parent) return
			canvas.width = parent.offsetWidth
			canvas.height = parent.offsetHeight
		}

		resize()
		window.addEventListener('resize', resize)

		// Create nodes
		const nodeCount = Math.floor(window.innerWidth / 40) // Increased node density
		const nodes: {
			x: number
			y: number
			vx: number
			vy: number
			radius: number
			color: string
		}[] = []

		// Use secondary color for some nodes
		const colors = [
			'rgba(150, 150, 150, 0.8)',
			'rgba(var(--color-secondary), 0.6)',
		]

		for (let i = 0; i < nodeCount; i++) {
			// Randomly choose between regular and secondary color
			const colorIndex = Math.random() > 0.7 ? 1 : 0

			nodes.push({
				x: Math.random() * canvas.width,
				y: Math.random() * canvas.height,
				vx: (Math.random() - 0.5) * 0.3, // Slightly faster movement
				vy: (Math.random() - 0.5) * 0.3,
				radius: Math.random() * 2.5 + 1.5, // Slightly larger nodes
				color: colors[colorIndex],
			})
		}

		const draw = () => {
			ctx.clearRect(0, 0, canvas.width, canvas.height)

			// Update and draw nodes
			nodes.forEach(node => {
				node.x += node.vx
				node.y += node.vy

				// Bounce off edges
				if (node.x < 0 || node.x > canvas.width) node.vx *= -1
				if (node.y < 0 || node.y > canvas.height) node.vy *= -1

				// Draw node
				ctx.beginPath()
				ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2)
				ctx.fillStyle = node.color
				ctx.fill()
			})

			// Draw connections
			nodes.forEach((node, i) => {
				nodes.forEach((otherNode, j) => {
					if (i !== j) {
						const dx = node.x - otherNode.x
						const dy = node.y - otherNode.y
						const distance = Math.sqrt(dx * dx + dy * dy)

						// Increase connection distance for more visibility
						const maxDistance = 120
						if (distance < maxDistance) {
							// Opacity based on distance
							const opacity = 0.5 * (1 - distance / maxDistance)

							// Use secondary color for connections between highlighted nodes
							let strokeColor = 'rgba(100, 100, 100, ' + opacity + ')'

							// If both nodes have the secondary color, make the connection use secondary color too
							if (
								node.color.includes('secondary') &&
								otherNode.color.includes('secondary')
							) {
								strokeColor = `rgba(var(--color-secondary), ${opacity * 1.5})`
							}

							ctx.beginPath()
							ctx.moveTo(node.x, node.y)
							ctx.lineTo(otherNode.x, otherNode.y)
							ctx.strokeStyle = strokeColor
							ctx.lineWidth = 0.7 // Slightly thicker lines
							ctx.stroke()
						}
					}
				})
			})

			requestAnimationFrame(draw)
		}

		draw()

		return () => {
			window.removeEventListener('resize', resize)
		}
	}, [])

	return (
		<canvas
			ref={canvasRef}
			className="absolute inset-0 -z-10 opacity-60"
			aria-hidden="true"
		/>
	)
}
